import React, { useState, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  DirectionsCar as CarIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Autocomplete, TextField, Chip, Switch, FormControlLabel } from '@mui/material';
import { FETCH_COURIER_SHIFT_HISTORY, FETCH_ALL_COURIERS, FETCH_ALL_SUPERVISORS } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';
import { formatTime, formatDate, formatDateToDDMMYY } from '../utils';
import DatePickerDDMMYY from '../../../components/common/DatePickerDDMMYY';
import { Badge } from '../../../pages/ShiftManagement/components/ui/Badge';
import { ComplianceReportDetailsModal } from './ComplianceReportDetailsModal';

interface ComplianceReportsViewProps {
  drivers: any[];
}

interface Courier {
  id: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  avatarUrl?: string;
  courierType?: string;
  city?: {
    id: string;
    name: string;
  };
}

interface Shift {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  shiftTemplate?: {
    id: string;
    name: string;
  } | null;
}

interface ShiftHistoryItem {
  id: string;
  shiftId: string;
  courierId: string;
  onlineHours: number;
  busyHours: number;
  idleHours: number;
  onlineSeconds: number;
  busySeconds: number;
  idleSeconds: number;
  onlinePercentage: number;
  acceptanceRate: number;
  acceptanceRateCompliant: boolean;
  onlineHoursCompliant: boolean;
  compliant: boolean;
  notificationsReceived: number;
  ordersAccepted: number;
  ordersRejected: number;
  createdAt: string;
  updatedAt: string;
  courier: Courier;
  shift: Shift;
}

interface ShiftHistoryData {
  items: ShiftHistoryItem[];
  totalCount: number;
  totalOnlineHours: number;
  totalBusyHours: number;
  totalIdleHours: number;
  averageAcceptanceRate: number;
  compliantShiftsCount: number;
}

const ITEMS_PER_PAGE = 20;
const STORAGE_KEY = 'complianceReportsViewState';

const loadCachedState = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('Error loading cached state:', error);
  }
  return null;
};

const saveCachedState = (state: any) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving state to cache:', error);
  }
};

export const ComplianceReportsView: React.FC<ComplianceReportsViewProps> = ({
  drivers,
}) => {
  const cachedState = loadCachedState();

  const [page, setPage] = useState(cachedState?.page || 0);
  const [selectedCourier, setSelectedCourier] = useState<{ id: string; label: string; firstName: string; lastName: string; mobileNumber: string } | null>(cachedState?.selectedCourier || null);
  const [driverId, setDriverId] = useState(cachedState?.driverId || '');
  const [debouncedDriverId, setDebouncedDriverId] = useState(cachedState?.debouncedDriverId || '');
  const [startDate, setStartDate] = useState<string>(cachedState?.startDate || '');
  const [endDate, setEndDate] = useState<string>(cachedState?.endDate || '');
  const [courierSearchQuery, setCourierSearchQuery] = useState('');
  const [courierPage, setCourierPage] = useState(0);
  const [allCouriers, setAllCouriers] = useState<Array<{
    id: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
  }>>([]);
  const [courierTotalCount, setCourierTotalCount] = useState(0);
  const [debouncedCourierSearchQuery, setDebouncedCourierSearchQuery] = useState('');
  const [selectedSupervisors, setSelectedSupervisors] = useState<Array<{ id: string; label: string; name: string; email: string }>>(cachedState?.selectedSupervisors || []);
  const [supervisorSearchQuery, setSupervisorSearchQuery] = useState('');
  const [debouncedSupervisorSearchQuery, setDebouncedSupervisorSearchQuery] = useState('');
  const [allSupervisors, setAllSupervisors] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [supervisorPage, setSupervisorPage] = useState(0);
  const [supervisorTotalCount, setSupervisorTotalCount] = useState(0);
  const [supervisorMode, setSupervisorMode] = useState(cachedState?.supervisorMode || false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const COURIERS_PER_PAGE = 20;
  const SUPERVISORS_PER_PAGE = 20;

  const [fetchShiftHistory, { data, loading, error }] = useLazyQuery<{ fetchCourierShiftHistory: ShiftHistoryData }>(
    FETCH_COURIER_SHIFT_HISTORY,
    {
      ...stagingOptions,
      fetchPolicy: 'network-only',
    }
  );

  const [fetchCouriers, { loading: couriersLoading }] = useLazyQuery<{
    fetchAllCouriers: {
      items: Array<{
        id: string;
        firstName: string;
        lastName: string;
        mobileNumber: string;
      }>;
      totalCount: number;
    };
  }>(FETCH_ALL_COURIERS, {
    ...stagingOptions,
    fetchPolicy: 'network-only',
  });

  const [fetchSupervisors, { loading: supervisorsLoading }] = useLazyQuery<{
    fetchAllSupervisors: {
      items: Array<{
        id: string;
        name: string;
        email: string;
      }>;
      totalCount: number;
    };
  }>(FETCH_ALL_SUPERVISORS, {
    ...stagingOptions,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    saveCachedState({
      page,
      selectedCourier,
      driverId,
      debouncedDriverId,
      startDate,
      endDate,
      selectedSupervisors,
      supervisorMode,
    });
  }, [page, selectedCourier, driverId, debouncedDriverId, startDate, endDate, selectedSupervisors, supervisorMode]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCourierSearchQuery(courierSearchQuery);
      setCourierPage(0);
      setAllCouriers([]);
    }, 500);

    return () => clearTimeout(timer);
  }, [courierSearchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSupervisorSearchQuery(supervisorSearchQuery);
      setSupervisorPage(0);
      setAllSupervisors([]);
    }, 500);

    return () => clearTimeout(timer);
  }, [supervisorSearchQuery]);

  useEffect(() => {
    const loadCouriers = async () => {
      try {
        const { data: couriersData } = await fetchCouriers({
          variables: {
            limit: COURIERS_PER_PAGE,
            offset: courierPage * COURIERS_PER_PAGE,
            query: debouncedCourierSearchQuery || undefined,
          },
        });

        if (couriersData?.fetchAllCouriers) {
          if (courierPage === 0) {
            setAllCouriers(couriersData.fetchAllCouriers.items);
          } else {
            setAllCouriers((prev) => [...prev, ...couriersData.fetchAllCouriers.items]);
          }
          setCourierTotalCount(couriersData.fetchAllCouriers.totalCount);
        }
      } catch (err) {
        console.error('Error fetching couriers:', err);
      }
    };

    loadCouriers();
  }, [debouncedCourierSearchQuery, courierPage, fetchCouriers]);

  useEffect(() => {
    const loadSupervisors = async () => {
      try {
        const { data: supervisorsData } = await fetchSupervisors({
          variables: {
            limit: SUPERVISORS_PER_PAGE,
            offset: supervisorPage * SUPERVISORS_PER_PAGE,
            query: debouncedSupervisorSearchQuery || undefined,
          },
        });

        if (supervisorsData?.fetchAllSupervisors) {
          if (supervisorPage === 0) {
            setAllSupervisors(supervisorsData.fetchAllSupervisors.items);
          } else {
            setAllSupervisors((prev) => [...prev, ...supervisorsData.fetchAllSupervisors.items]);
          }
          setSupervisorTotalCount(supervisorsData.fetchAllSupervisors.totalCount);
        }
      } catch (err) {
        console.error('Error fetching supervisors:', err);
      }
    };

    loadSupervisors();
  }, [debouncedSupervisorSearchQuery, supervisorPage, fetchSupervisors]);

  useEffect(() => {
    if (selectedCourier) {
      setDriverId(selectedCourier.id);
      setDebouncedDriverId(selectedCourier.id);
      setPage(0);
    } else {
      setDriverId('');
      setDebouncedDriverId('');
      setPage(0);
      setStartDate('');
      setEndDate('');
    }
  }, [selectedCourier]);

  useEffect(() => {
    if (supervisorMode) {
      if (selectedSupervisors.length > 0) {
        const startDateISO = startDate ? new Date(startDate).toISOString() : undefined;
        const endDateISO = endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined;
        const supervisorIds = selectedSupervisors.map(s => s.id);

        fetchShiftHistory({
          variables: {
            courierId: undefined,
            supervisorIds: supervisorIds,
            limit: ITEMS_PER_PAGE,
            offset: page * ITEMS_PER_PAGE,
            startDate: startDateISO,
            endDate: endDateISO,
          },
        }).catch((err) => {
          console.error('Error fetching shift history:', err);
        });
      }
    } else {
      if (debouncedDriverId && debouncedDriverId.trim()) {
        const startDateISO = startDate ? new Date(startDate).toISOString() : undefined;
        const endDateISO = endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined;
        const supervisorIds = selectedSupervisors.length > 0
          ? selectedSupervisors.map(s => s.id)
          : undefined;

        fetchShiftHistory({
          variables: {
            courierId: debouncedDriverId.trim(),
            supervisorIds: supervisorIds,
            limit: ITEMS_PER_PAGE,
            offset: page * ITEMS_PER_PAGE,
            startDate: startDateISO,
            endDate: endDateISO,
          },
        }).catch((err) => {
          console.error('Error fetching shift history:', err);
        });
      }
    }
  }, [supervisorMode, debouncedDriverId, selectedSupervisors, page, startDate, endDate, fetchShiftHistory]);

  const getDriverName = (driverId: string) => {
    const courier = allCouriers.find((c) => c.id === driverId);
    if (courier) {
      const firstName = courier.firstName || '';
      const lastName = courier.lastName || '';
      const name = `${firstName} ${lastName}`.trim();
      if (name) return name;
    }

    const driver = drivers?.find((d) => d.id === driverId);
    if (driver) {
      const firstName = driver.firstName || '';
      const lastName = driver.lastName || '';
      const name = `${firstName} ${lastName}`.trim();
      if (name) return name;
    }

    return driverId ? `ID: ${driverId}` : 'ID: Unknown';
  };

  const shiftHistoryData = data?.fetchCourierShiftHistory;
  const items = shiftHistoryData?.items || [];
  const totalCount = shiftHistoryData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const selectedReport = useMemo(() => {
    return items.find(item => item.id === selectedReportId) || null;
  }, [items, selectedReportId]);

  const stats = useMemo(() => {
    if (!shiftHistoryData) {
      return {
        total: 0,
        compliant: 0,
        nonCompliant: 0,
        totalOnlineHours: 0,
        totalBusyHours: 0,
        totalIdleHours: 0,
        averageAcceptanceRate: 0,
      };
    }

    return {
      total: shiftHistoryData.totalCount,
      compliant: shiftHistoryData.compliantShiftsCount,
      nonCompliant: shiftHistoryData.totalCount - shiftHistoryData.compliantShiftsCount,
      totalOnlineHours: shiftHistoryData.totalOnlineHours,
      totalBusyHours: shiftHistoryData.totalBusyHours,
      totalIdleHours: shiftHistoryData.totalIdleHours,
      averageAcceptanceRate: shiftHistoryData.averageAcceptanceRate,
    };
  }, [shiftHistoryData]);

  useEffect(() => {
    if (page >= totalPages && totalPages > 0) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, page]);

  const handleClearDriverId = () => {
    setSelectedCourier(null);
    setDriverId('');
    setDebouncedDriverId('');
    setPage(0);
    setStartDate('');
    setEndDate('');
  };

  const courierOptions = useMemo(() => {
    if (!allCouriers || allCouriers.length === 0) return [];
    return allCouriers.map((courier) => ({
      id: courier.id,
      label: `${courier.firstName} ${courier.lastName} (${courier.mobileNumber}) - ID: ${courier.id}`,
      firstName: courier.firstName,
      lastName: courier.lastName,
      mobileNumber: courier.mobileNumber,
    }));
  }, [allCouriers]);

  const hasMoreCouriers = allCouriers.length < courierTotalCount;
  const hasMoreSupervisors = allSupervisors.length < supervisorTotalCount;

  const handleLoadMoreCouriers = () => {
    if (!couriersLoading && hasMoreCouriers) {
      setCourierPage((prev) => prev + 1);
    }
  };

  const handleLoadMoreSupervisors = () => {
    if (!supervisorsLoading && hasMoreSupervisors) {
      setSupervisorPage((prev) => prev + 1);
    }
  };

  const supervisorOptions = useMemo(() => {
    if (!allSupervisors || allSupervisors.length === 0) return [];
    return allSupervisors.map((supervisor) => ({
      id: supervisor.id,
      label: supervisor.name || supervisor.email || supervisor.id,
      name: supervisor.name,
      email: supervisor.email,
    }));
  }, [allSupervisors]);

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  return (
    <div className="main-wrapper">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="pageTitle mb-1 text-xl font-bold">Compliance Reports</h2>
          <Tooltip title="Monitor driver performance and compliance status by selecting a driver" arrow>
            <InfoIcon sx={{ fontSize: '18px', color: '#9CA3AF', cursor: 'help' }} />
          </Tooltip>
        </div>
        <p className="text-sm text-gray-500">Select a driver from the dropdown to view their shift history and compliance metrics</p>
      </div>

      {/* Statistics Cards */}
      {((!supervisorMode && debouncedDriverId) || (supervisorMode && selectedSupervisors.length > 0)) && shiftHistoryData && (
        <div className="flex items-center gap-3 flex-wrap mb-6">
          <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
              <ScheduleIcon sx={{ fontSize: '20px', color: '#3B82F6' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Total Shift Sessions</p>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center shadow-sm">
              <CheckCircleIcon sx={{ fontSize: '20px', color: '#10B981' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Compliant</p>
              <p className="text-xl font-bold text-gray-900">{stats.compliant}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-white to-red-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center shadow-sm">
              <CloseIcon sx={{ fontSize: '20px', color: '#EF4444' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Non-Compliant</p>
              <p className="text-xl font-bold text-gray-900">{stats.nonCompliant}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
              <CheckCircleIcon sx={{ fontSize: '20px', color: '#3B82F6' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Total Online</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalOnlineHours.toFixed(1)}h</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-white to-amber-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center shadow-sm">
              <CarIcon sx={{ fontSize: '20px', color: '#F59E0B' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Total Busy</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalBusyHours.toFixed(1)}h</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shadow-sm">
              <AccessTimeIcon sx={{ fontSize: '20px', color: '#6B7280' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Total Idle</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalIdleHours.toFixed(1)}h</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-white to-purple-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center shadow-sm">
              <TrendingUpIcon sx={{ fontSize: '20px', color: '#9333EA' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Avg Acceptance</p>
              <p className="text-xl font-bold text-gray-900">{stats.averageAcceptanceRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Filter */}
      <div className="bg-gradient-to-r from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm p-5 mb-6">
        <div className="flex items-center justify-end mb-4 pb-4 border-b border-gray-200/60">
          <FormControlLabel
            control={
              <Switch
                checked={supervisorMode}
                onChange={(e) => {
                  setSupervisorMode(e.target.checked);
                  // Clear selections when switching modes
                  if (e.target.checked) {
                    setSelectedCourier(null);
                    setDriverId('');
                    setDebouncedDriverId('');
                  } else {
                    setSelectedSupervisors([]);
                  }
                  setPage(0);
                }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#3B82F6',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#3B82F6',
                  },
                }}
              />
            }
            label={
              <span className="text-sm font-semibold text-gray-700">
                Supervisor Mode
              </span>
            }
          />
        </div>

        <div className="flex items-center flex-wrap gap-3">
          {!supervisorMode ? (
            <>
              <div className="flex items-center gap-2 mr-2">
                <SearchIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                <span className="text-xs font-semibold text-gray-700">Select Driver:</span>
              </div>

              <Tooltip title="Select a driver to view their shift history and compliance reports" arrow placement="top">
                <div className="flex-1 max-w-md">
                  <Autocomplete
                    options={courierOptions}
                    getOptionLabel={(option) => option.label}
                    value={selectedCourier}
                    onChange={(event, newValue) => {
                      setSelectedCourier(newValue);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setCourierSearchQuery(newInputValue);
                    }}
                    loading={couriersLoading}
                    ListboxProps={{
                      onScroll: (event: React.SyntheticEvent) => {
                        const listboxNode = event.currentTarget;
                        if (
                          listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                          hasMoreCouriers &&
                          !couriersLoading
                        ) {
                          handleLoadMoreCouriers();
                        }
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select a driver..."
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '5px',
                            '& fieldset': {
                              borderRadius: '5px',
                            },
                            '&:hover': {
                              backgroundColor: 'white',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                            },
                          },
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        paddingRight: '14px !important',
                      },
                    }}
                  />
                </div>
              </Tooltip>

              {selectedCourier && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                  <PersonIcon sx={{ fontSize: '16px' }} />
                  <span>{selectedCourier.firstName} {selectedCourier.lastName} (ID: {selectedCourier.id})</span>
                  <Tooltip title="Clear selection" arrow>
                    <button
                      onClick={handleClearDriverId}
                      className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                    >
                      <CloseIcon sx={{ fontSize: '16px' }} />
                    </button>
                  </Tooltip>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mr-2">
                <PersonIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                <span className="text-xs font-semibold text-gray-700">By Supervisors:</span>
              </div>

              <Tooltip title="Select one or more supervisors to filter shift history" arrow placement="top">
                <div className="flex-1 max-w-md">
                  <Autocomplete
                    multiple
                    options={supervisorOptions}
                    getOptionLabel={(option) => option.label}
                    value={selectedSupervisors}
                    onChange={(event, newValue) => {
                      setSelectedSupervisors(newValue);
                      setPage(0);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setSupervisorSearchQuery(newInputValue);
                    }}
                    loading={supervisorsLoading}
                    ListboxProps={{
                      onScroll: (event: React.SyntheticEvent) => {
                        const listboxNode = event.currentTarget;
                        if (
                          listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                          hasMoreSupervisors &&
                          !supervisorsLoading
                        ) {
                          handleLoadMoreSupervisors();
                        }
                      },
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.label}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            color: '#3B82F6',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            '& .MuiChip-deleteIcon': {
                              color: '#3B82F6',
                              '&:hover': {
                                color: '#2563EB',
                              },
                            },
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select supervisors..."
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '5px',
                            '& fieldset': {
                              borderRadius: '5px',
                            },
                            '&:hover': {
                              backgroundColor: 'white',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                            },
                          },
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        paddingRight: '14px !important',
                      },
                    }}
                  />
                </div>
              </Tooltip>

              {selectedSupervisors.length > 0 && (
                <Tooltip title="Clear supervisor selection" arrow>
                  <button
                    onClick={() => {
                      setSelectedSupervisors([]);
                      setPage(0);
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium hover:bg-gray-100/80 rounded-lg transition-all shadow-sm hover:shadow"
                  >
                    Clear
                  </button>
                </Tooltip>
              )}
            </>
          )}
        </div>

        {/* Date Filters */}
        {((!supervisorMode && debouncedDriverId) || (supervisorMode && selectedSupervisors.length > 0)) && (
          <div className="mt-4 pt-4 border-t border-gray-200/60">
            <div className="flex items-center flex-wrap gap-3">
              <div className="flex items-center gap-2 mr-2">
                <CalendarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                <span className="text-xs font-semibold text-gray-700">Date Range:</span>
              </div>

              <DatePickerDDMMYY
                value={startDate}
                onChange={(value) => {
                  setStartDate(value);
                  setPage(0);
                }}
                placeholder="Start Date: dd/mm/yy"
                tooltip="Filter shifts by start date. Click to open calendar picker."
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      backgroundColor: 'white',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              />

              <span className="text-sm text-gray-500">to</span>

              <DatePickerDDMMYY
                value={endDate}
                onChange={(value) => {
                  setEndDate(value);
                  setPage(0);
                }}
                placeholder="End Date: dd/mm/yy"
                tooltip="Filter shifts by end date. Click to open calendar picker. Must be after start date."
                minDate={startDate}
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      backgroundColor: 'white',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              />

              {(startDate || endDate) && (
                <Tooltip title="Clear date filters" arrow>
                  <button
                    onClick={handleClearDates}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium hover:bg-gray-100/80 rounded-lg transition-all shadow-sm hover:shadow"
                  >
                    Clear Dates
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <TableContainer className="w-full" sx={{ overflowX: 'auto' }}>
        <Table
          stickyHeader
          sx={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0 10px',
            '& thead th': {
              borderBottom: 'none',
              fontSize: '12px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              backgroundColor: 'rgba(249, 250, 251, 0.8)',
            },
          }}
          aria-label="compliance reports table"
        >
          <TableHead>
            <TableRow className='bg-gray-400 rounded-lg'>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%', minWidth: '120px' }}>Driver</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '11%', minWidth: '160px' }}>Shift Session</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '7%', minWidth: '90px' }}>Online</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '7%', minWidth: '90px' }}>Busy</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '7%', minWidth: '90px' }}>Idle</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '7%', minWidth: '100px' }}>Online %</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%', minWidth: '110px' }}>Acceptance</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '9%', minWidth: '120px' }}>Orders</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%', minWidth: '140px' }}>Compliance</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '9%', minWidth: '120px' }}>Created</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '9%', minWidth: '120px' }}>Updated</TableCell>
              <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" align="right" sx={{ width: '4%', minWidth: '80px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} align="center" className="py-12">
                  <CircularProgress size={48} sx={{ color: '#3B82F6', mb: 2 }} />
                  <p className="text-sm font-medium text-gray-600">Loading</p>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={12} align="center" className="py-12">
                  <AssessmentIcon sx={{ fontSize: '48px', color: '#EF4444', marginBottom: '8px', display: 'block', margin: '0 auto 8px' }} />
                  <p className="text-sm font-medium text-gray-600">Error loading data</p>
                  <p className="text-xs text-gray-400 mt-1">{error.message}</p>
                </TableCell>
              </TableRow>
            ) : (!supervisorMode && !debouncedDriverId) || (supervisorMode && selectedSupervisors.length === 0) ? (
              <TableRow>
                <TableCell colSpan={12} align="center" className="py-12">
                  <SearchIcon sx={{ fontSize: '48px', color: '#9CA3AF', marginBottom: '8px', display: 'block', margin: '0 auto 8px' }} />
                  <p className="text-sm font-medium text-gray-600">
                    {supervisorMode ? 'Select Supervisors' : 'Select a Driver'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {supervisorMode
                      ? 'Select one or more supervisors from the dropdown above to view their shift history and compliance reports'
                      : 'Select a driver from the dropdown above to view their shift history and compliance reports'
                    }
                  </p>
                </TableCell>
              </TableRow>
            ) : !shiftHistoryData ? (
              <TableRow>
                <TableCell colSpan={12} align="center" className="py-12">
                  <AssessmentIcon sx={{ fontSize: '48px', color: '#9CA3AF', marginBottom: '8px', display: 'block', margin: '0 auto 8px' }} />
                  <p className="text-sm font-medium text-gray-600">No data available</p>
                  <p className="text-xs text-gray-400 mt-1">Waiting for data...</p>
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" className="py-12">
                  <AssessmentIcon sx={{ fontSize: '48px', color: '#9CA3AF', marginBottom: '8px', display: 'block', margin: '0 auto 8px' }} />
                  <p className="text-sm font-medium text-gray-600">No shift history found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {supervisorMode
                      ? 'No shift history available for the selected supervisors'
                      : `No shift history available for driver ID: ${debouncedDriverId}`
                    }
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const isCompliant = item.compliant;
                const getComplianceBadgeVariant = (): 'success' | 'destructive' => {
                  return isCompliant ? 'success' : 'destructive';
                };

                return (
                  <TableRow
                    key={item.id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '& td, & th': { borderBottom: 'none' },
                      '& td:first-of-type, & th:first-of-type': {
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: 12,
                      },
                      '& td:last-of-type, & th:last-of-type': {
                        borderTopRightRadius: 12,
                        borderBottomRightRadius: 12,
                      },
                      '&:hover': {
                        background: 'linear-gradient(90deg, rgba(75, 85, 99, 0.15) 0%, rgba(75, 85, 99, 0.08) 100%)',
                        boxShadow: '0 4px 16px rgba(75, 85, 99, 0.25)',
                        '& td:first-of-type, & th:first-of-type': {
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                        },
                        '& td:last-of-type, & th:last-of-type': {
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                        },
                      },
                      position: 'relative',
                      transition: 'all 0.2s ease-in-out',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 24,
                        right: 24,
                        bottom: -5,
                        height: '1px',
                        backgroundColor: 'rgba(226, 232, 240, 0.9)',
                      }
                    }}
                    className={`bg-white border border-gray-100 ${!isCompliant ? 'border-l-4 border-l-red-500' : ''}`}
                  >
                    <TableCell component="th" scope="row" className="py-2.5" sx={{ overflow: 'hidden' }}>
                      <div className="flex flex-col leading-tight min-w-0">
                        <span className="font-semibold text-sm text-gray-900 truncate block min-w-0">
                          {item.courier
                            ? `${item.courier.firstName || ''} ${item.courier.lastName || ''}`.trim() || getDriverName(item.courierId)
                            : getDriverName(item.courierId)
                          }
                        </span>
                        <span className="text-[11px] font-mono text-gray-500 uppercase tracking-wide mt-0.5">
                          ID: {item.courierId || 'Unknown'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          Shift Session {item.shiftId.slice(0, 8)}
                        </span>
                        {item.shift.shiftTemplate && (
                          <span className="text-xs font-bold text-primaryColor bg-primaryColor/10 px-2 py-0.5 rounded-md mt-0.5 inline-flex flex-wrap gap-1 items-center">
                            <span className="opacity-80">ID:</span>
                            <span className="font-mono">
                              {item.shift.shiftTemplate.id.slice(0, 8)}
                            </span>
                            <span className="opacity-60">•</span>
                            <span className="opacity-80">Shift Schedule:</span>
                            <span>
                              {item.shift.shiftTemplate.name || 'N/A'}
                            </span>
                          </span>
                        )}
                        <span className="text-xs text-gray-500 mt-0.5">
                          {formatDate(item.shift.startTime)}
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          {formatTime(item.shift.startTime)} - {formatTime(item.shift.endTime)}
                        </span>
                        <span className={`text-xs mt-0.5 font-medium ${item.shift.status === 'COMPLETED' ? 'text-green-600' :
                          item.shift.status === 'ACTIVE' ? 'text-blue-600' :
                            item.shift.status === 'CANCELLED' ? 'text-red-600' :
                              'text-gray-600'
                          }`}>
                          {item.shift.status}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-900">
                          {item.onlineHours.toFixed(1)}h
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          {Math.floor(item.onlineSeconds / 60)}m
                        </span>
                        {item.onlineHoursCompliant && (
                          <span className="text-xs text-green-600 mt-0.5">✓ Compliant</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-900">
                          {item.busyHours.toFixed(1)}h
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          {Math.floor(item.busySeconds / 60)}m
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-900">
                          {item.idleHours.toFixed(1)}h
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          {Math.floor(item.idleSeconds / 60)}m
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className={`text-sm font-semibold ${item.onlinePercentage >= 95 ? 'text-green-700' : 'text-red-700'
                          }`}>
                          {item.onlinePercentage.toFixed(1)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className={`text-sm font-bold ${item.acceptanceRate >= 90 ? 'text-green-700' : 'text-red-700'
                          }`}>
                          {item.acceptanceRate.toFixed(1)}%
                        </span>
                        {item.acceptanceRateCompliant && (
                          <span className="text-xs text-green-600 mt-0.5">✓ Compliant</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-green-600">
                          Accepted: {item.ordersAccepted}
                        </span>
                        <span className="text-sm font-semibold text-red-600">
                          Rejected: {item.ordersRejected}
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          Notifications: {item.notificationsReceived}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col gap-1">
                        <Badge variant={getComplianceBadgeVariant()}>
                          {isCompliant ? 'Compliant' : 'Non-Compliant'}
                        </Badge>
                        {!isCompliant && (
                          <div className="text-xs text-red-600">
                            {!item.onlineHoursCompliant && <div>✗ Online Hours</div>}
                            {!item.acceptanceRateCompliant && <div>✗ Acceptance</div>}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-900">
                          {formatDate(item.createdAt)}
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          {formatTime(item.createdAt)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-900">
                          {formatDate(item.updatedAt)}
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          {formatTime(item.updatedAt)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell align="right" className="py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <Tooltip title="View Details" arrow>
                          <button
                            onClick={() => {
                              setSelectedReportId(item.id);
                              setModalOpen(true);
                            }}
                            className="text-primaryColor hover:text-darkBlue transition-colors p-1.5 hover:bg-gray-100 rounded"
                          >
                            <VisibilityIcon sx={{ fontSize: '18px' }} />
                          </button>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {((!supervisorMode && debouncedDriverId) || (supervisorMode && selectedSupervisors.length > 0)) && totalCount > 0 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {page * ITEMS_PER_PAGE + 1} to {Math.min((page + 1) * ITEMS_PER_PAGE, totalCount)} of {totalCount} shift{totalCount !== 1 ? 's' : ''}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page === 0
                  ? 'bg-white text-gray-400 cursor-not-allowed border border-gray-200'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
              >
                Previous
              </button>
              {(() => {
                const pagesToShow = [];
                const maxPagesToShow = 5;
                let startPage = Math.max(0, page - Math.floor(maxPagesToShow / 2));
                let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

                if (endPage - startPage < maxPagesToShow - 1) {
                  startPage = Math.max(0, endPage - maxPagesToShow + 1);
                }

                for (let i = startPage; i <= endPage; i++) {
                  pagesToShow.push(i);
                }

                return pagesToShow.map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page === pageNum
                      ? 'bg-primaryColor text-white border border-primaryColor'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                  >
                    {pageNum + 1}
                  </button>
                ));
              })()}
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page >= totalPages - 1
                  ? 'bg-white text-gray-400 cursor-not-allowed border border-gray-200'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Compliance Rules */}
      <div className="mt-6 bg-blue-50/80 border border-blue-200/60 rounded-lg p-4 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Compliance Rules</h3>
        <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
          <li>
            <strong>Online Duration:</strong> Must be at least 95% of the shift duration
          </li>
          <li>
            <strong>Acceptance Rate:</strong> Must be at least 90%
          </li>
        </ul>
      </div>

      {/* Report Details Modal */}
      <ComplianceReportDetailsModal
        open={modalOpen}
        report={selectedReport}
        onClose={() => {
          setModalOpen(false);
          setSelectedReportId(null);
        }}
      />
    </div>
  );
};
